import React from 'react';
import { StyleSheet, TextInput, TouchableOpacity, View } from 'react-native';
import { useTranslation } from 'react-i18next';
import Text from '../global/Text';
import theme from '../../constants/theme';
import fontMaker from '../../utils/font';
import { NpsQuestion } from '../../store/reducers/nps/type';
import type { ChecklistAnswer } from './npsResponseDetails';

const NPS_OTHER_MARKER = '__NPS_OTHER__::';

const getOptionLabel = (option: string): string =>
  option.startsWith(NPS_OTHER_MARKER) ? option.slice(NPS_OTHER_MARKER.length) : option;

const isOtherOption = (option: string): boolean =>
  option.startsWith(NPS_OTHER_MARKER) ||
  ['lainnya', 'otros', 'outros', '기타', 'ផ្សេងៗ'].some(s => option.toLowerCase().includes(s));

type Props = {
  question: NpsQuestion;
  value: ChecklistAnswer;
  onChange: (updated: ChecklistAnswer) => void;
};

const NPSQuestionChecklist = ({ question, value, onChange }: Props) => {
  const { t } = useTranslation('common');
  const checklists = question.checklists ?? [];

  const regularOptions = checklists.filter(c => !isOtherOption(c));
  const otherOption = checklists.find(c => isOtherOption(c));

  const toggle = (key: string, label: string, checked: boolean) => {
    const next = { ...value };
    if (checked) {
      next[key] = { key, value: label };
    } else {
      delete next[key];
    }
    onChange(next);
  };

  const otherKey = otherOption || 'other';
  const otherSelected = Boolean(value[otherKey]);

  return (
    <View style={styles.root}>
      <Text style={styles.questionText}>{question.content}</Text>
      {regularOptions.map(option => {
        const key = option;
        const selected = Boolean(value[key]);
        return (
          <TouchableOpacity
            key={key}
            style={styles.row}
            onPress={() => toggle(key, getOptionLabel(option), !selected)}
            activeOpacity={0.7}
          >
            <View style={[styles.checkbox, selected && styles.checkboxSelected]}>
              {selected && <Text style={styles.checkmark}>✓</Text>}
            </View>
            <Text style={styles.optionLabel}>{getOptionLabel(option)}</Text>
          </TouchableOpacity>
        );
      })}

      {otherOption && (
        <>
          <TouchableOpacity
            style={styles.row}
            onPress={() =>
              otherSelected
                ? onChange({ ...value, [otherKey]: undefined })
                : onChange({ ...value, [otherKey]: { key: otherKey, value: '' } })
            }
            activeOpacity={0.7}
          >
            <View style={[styles.checkbox, otherSelected && styles.checkboxSelected]}>
              {otherSelected && <Text style={styles.checkmark}>✓</Text>}
            </View>
            <Text style={styles.optionLabel}>{t('nps.othersSpecify')}</Text>
          </TouchableOpacity>
          {otherSelected && (
            <TextInput
              style={styles.otherInput}
              value={value[otherKey]?.value ?? ''}
              onChangeText={text => onChange({ ...value, [otherKey]: { key: otherKey, value: text } })}
              placeholder={t('nps.enterDescription')}
              multiline
            />
          )}
        </>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  root: { flex: 1, padding: 16 },
  questionText: {
    ...fontMaker({ weight: 'SemiBold' }),
    fontSize: 15,
    color: theme.colors.darkgray,
    marginBottom: 16,
    lineHeight: 22
  },
  row: { flexDirection: 'row', alignItems: 'flex-start', marginBottom: 12 },
  checkbox: {
    width: 22,
    height: 22,
    borderRadius: 4,
    borderWidth: 1.5,
    borderColor: theme.colors.gray,
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 10,
    marginTop: 1,
    flexShrink: 0
  },
  checkboxSelected: {
    backgroundColor: theme.colors.primary,
    borderColor: theme.colors.primary
  },
  checkmark: { color: '#fff', fontSize: 13, lineHeight: 16 },
  optionLabel: {
    flex: 1,
    fontSize: 14,
    color: theme.colors.darkgray,
    ...fontMaker({ weight: 'Normal' }),
    lineHeight: 20
  },
  otherInput: {
    borderWidth: 1,
    borderColor: theme.colors.gray,
    borderRadius: 8,
    padding: 10,
    minHeight: 80,
    fontSize: 14,
    color: theme.colors.darkgray,
    ...fontMaker({ weight: 'Normal' }),
    marginTop: 4,
    marginLeft: 32,
    textAlignVertical: 'top'
  }
});

export default NPSQuestionChecklist;
